import React, { useEffect, useState } from 'react';

import validate from '@vlabs/shared/validators';
import { Control, Divider, ButtonSwitchGroup, ButtonSwitch } from '@vlabs/uikit';
import PropTypes from 'prop-types';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CustomFilterPopover } from '../components/CustomFilterPopover';
import { hasAnyError } from '../helpers';
import { useFilterCount } from '../hooks';
import st from '../shared.module.sass';
import { MapWrapper } from './map-wrapper/MapWrapper';

const TABS = {
  map: 'map',
  address: 'address',
  coordinates: 'coordinates',
};

export const GeolocationFilter = ({
  hasMap = false,
  prefixId = '',
  prefixName = '',
  onCountChange = undefined,
  ...props
}) => {
  const { t } = useTranslation();
  const { register, formState: { errors }, setValue } = useFormContext();

  const [activeTab, setActiveTab] = useState(undefined);
  const [mapResetTick, setMapResetTick] = useState(0);

  const withPrefixName = (value) => [prefixName, value].filter(Boolean).join('.');
  const withPrefixId = (value) => [prefixId, value].filter(Boolean).join('.');

  useEffect(() => {
    if (hasMap) {
      setActiveTab(TABS.map);
      return;
    }
    setActiveTab(TABS.address);
  }, [hasMap]);

  const geolocationFields = [
    'cities', 'areas', 'districts', 'streets', 'house_numbers',
    'origin_longitude', 'longitude_delta', 'origin_latitude', 'latitude_delta',
  ];
  const fieldsWithPrefix = geolocationFields.map((name) => withPrefixName(name));

  const watchedFields = useWatch({
    name: [
      withPrefixName('cities'),
      withPrefixName('areas'),
      withPrefixName('districts'),
      withPrefixName('streets'),
      withPrefixName('house_numbers'),
      withPrefixName('origin_longitude'),
      withPrefixName('longitude_delta'),
      withPrefixName('origin_latitude'),
      withPrefixName('latitude_delta'),
    ],
  });
  const [
    cities,
    areas,
    districts,
    streets,
    houseNumbers,
    longitude,
    longitudeDelta,
    latitude,
    latitudeDelta,
  ] = watchedFields;

  const filterCount = [
    cities,
    areas,
    districts,
    streets,
    houseNumbers,
    latitude,
    latitudeDelta,
    longitude,
    longitudeDelta,
  ].filter(Boolean).length > 0 ? 1 : 0;
  useFilterCount('location', filterCount, onCountChange);

  const handleReset = () => {
    fieldsWithPrefix.forEach((field) => {
      setValue(field, '');
    });
    setMapResetTick((x) => x + 1);
  };
  return (
    <CustomFilterPopover
      className={st.wrapper_map}
      hasError={hasAnyError(errors, fieldsWithPrefix)}
      hasFilterValue={Boolean(filterCount)}
      testId="geolocationFilter"
      triggerValue={t('location')}
      {...props}
    >
      <ButtonSwitchGroup
        kind="secondary"
        onChange={setActiveTab}
        size="small"
        testId="geolocationFiltersSwitch"
        value={activeTab}
        variant="square"
      >
        {hasMap && (
          <ButtonSwitch
            value="map"
            variant="invisible"
          >
            {t('map')}
          </ButtonSwitch>
        )}

        <ButtonSwitch
          value="address"
          variant="invisible"
        >
          {t('address')}
        </ButtonSwitch>
        <ButtonSwitch
          value="coordinates"
          variant="invisible"
        >
          {t('coordinates')}
        </ButtonSwitch>
      </ButtonSwitchGroup>

      <Divider small />

      {activeTab === 'map' && (
        <MapWrapper
          key={mapResetTick}
          prefixId={prefixId}
          prefixName={prefixName}
        />
      )}

      {activeTab === 'address' && (
        <>
          <Control.Input
            id={withPrefixId('cities')}
            label={t('geoposition.city')}
            {...register(withPrefixName('cities'), {
              maxLength: validate.maxLength(36),
            })}
            errors={errors}
            isClearable
            onClear={() => setValue(withPrefixName('cities'), '')}
          />
          <Divider small />

          <Control.Input
            id={withPrefixId('areas')}
            label={t('geoposition.area')}
            {...register(withPrefixName('areas'), {
              maxLength: validate.maxLength(36),
            })}
            errors={errors}
            isClearable
            onClear={() => setValue(withPrefixName('areas'), '')}
          />
          <Divider small />

          <Control.Input
            id={withPrefixId('districts')}
            label={t('geoposition.district')}
            {...register(withPrefixName('districts'), {
              maxLength: validate.maxLength(36),
            })}
            errors={errors}
            isClearable
            onClear={() => setValue(withPrefixName('districts'), '')}
          />
          <Divider small />

          <div className={st.flex}>
            <div className={st.width_small}>
              <Control.Input
                id={withPrefixId('streets')}
                label={t('geoposition.street')}
                {...register(withPrefixName('streets'), {
                  maxLength: validate.maxLength(36),
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('streets'), '')}
              />
            </div>
            <Divider small />
            <div className={st.width_big}>
              <Control.Input
                id={withPrefixId('house_numbers')}
                label={t('geoposition.house_number')}
                {...register(withPrefixName('house_numbers'), {
                  maxLength: validate.maxLength(36),
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('house_numbers'), '')}
              />
            </div>
          </div>
        </>
      )}

      {activeTab === 'coordinates' && (
        <>
          <div className={st.flex}>
            <div className={st.width}>
              <Control.Input
                id={withPrefixId('origin_longitude')}
                label={t('longitude_range')}
                {...register(withPrefixName('origin_longitude'), {
                  min: validate.gte(-180),
                  max: validate.lte(180),
                  validate: {
                    number: validate.number(),
                    validateLatitude: (value) => {
                      if (!value && latitude) {
                        return validate.required();
                      }
                      return true;
                    },
                    validateDelta: (value) => {
                      if (!value && longitudeDelta) {
                        return validate.required();
                      }
                      return true;
                    },
                  },
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('origin_longitude'), '')}
              />
            </div>
            <Divider small />
            <div className={st.width}>
              <Control.Input
                id={withPrefixId('longitude_delta')}
                label={t('accuracy_range')}
                {...register(withPrefixName('longitude_delta'), {
                  min: validate.gte(0),
                  max: validate.lte(90),
                  validate: validate.number(),
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('longitude_delta'), '')}
              />
            </div>
          </div>
          <Divider small />
          <div className={st.flex}>
            <div className={st.width}>
              <Control.Input
                id={withPrefixId('origin_latitude')}
                label={t('latitude_range')}
                {...register(withPrefixName('origin_latitude'), {
                  min: validate.gte(-90),
                  max: validate.lte(90),
                  validate: {
                    number: validate.number(),
                    validateLongitude: (value) => {
                      if (!value && longitude) {
                        return validate.required();
                      }
                      return true;
                    },
                    validateDelta: (value) => {
                      if (!value && latitudeDelta) {
                        return validate.required();
                      }
                      return true;
                    },
                  },
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('origin_latitude'), '')}
              />
            </div>
            <Divider small />
            <div className={st.width}>
              <Control.Input
                id={withPrefixId('latitude_delta')}
                label={t('accuracy_range')}
                {...register(withPrefixName('latitude_delta'), {
                  min: validate.gte(0),
                  max: validate.lte(90),
                  validate: validate.number(),
                })}
                errors={errors}
                isClearable
                onClear={() => setValue(withPrefixName('latitude_delta'), '')}
              />
            </div>
          </div>
        </>
      )}

      <Divider small />

      <div className={st.ButtonPanel}>
        <Control.Button
          disabled={filterCount === 0}
          kind="negative"
          onClick={handleReset}
          size="small"
          variant="filled"
        >
          {t('button.clear')}
        </Control.Button>
      </div>
    </CustomFilterPopover>
  );
};

GeolocationFilter.propTypes = {
  hasMap: PropTypes.bool,
  prefixId: PropTypes.string,
  prefixName: PropTypes.string,
  onCountChange: PropTypes.func,
};
